import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { useSelector } from 'react-redux';
import { getMyTasks, addAssigneeToTask, deleteTask, searchUsers, getUserId, getTaskById, updateTask, createTask, getProjects, getTaskLists } from '../../../utils/api';
import TaskDetailSidePanel from './TaskDetailSidePanel';
import EditableTaskRow from './EditableTaskRow';
import Pagination from '../../common/Pagination';
import PriorityDropdown from '../../common/PriorityDropdown';
import './SingleProjectView.scss';

// Default Task Input Row Component for MyTasksView
const DefaultTaskInputRow = forwardRef(({ projectId, taskListId, project, onSave, searchUsers, onCancel }, ref) => {
    const [taskTitle, setTaskTitle] = useState('');
    const [assignee, setAssignee] = useState(null);
    const [dueDate, setDueDate] = useState('');
    const [priority, setPriority] = useState(0);
    const [assigneeSearchQuery, setAssigneeSearchQuery] = useState('');
    const [assigneeSearchResults, setAssigneeSearchResults] = useState([]);
    const [showAssigneeDropdown, setShowAssigneeDropdown] = useState(false);
    const [showDueDatePicker, setShowDueDatePicker] = useState(false);
    const [selectedProjectId, setSelectedProjectId] = useState(projectId || null);
    const [selectedTaskListId, setSelectedTaskListId] = useState(taskListId || null);
    const [selectedProject, setSelectedProject] = useState(project || null);
    const [projects, setProjects] = useState([]);
    const [taskLists, setTaskLists] = useState([]);
    const [showProjectDropdown, setShowProjectDropdown] = useState(false);
    const [showTaskListDropdown, setShowTaskListDropdown] = useState(false);
    const [isLoadingProjects, setIsLoadingProjects] = useState(false);
    const [isLoadingTaskLists, setIsLoadingTaskLists] = useState(false);
    const projectDropdownRef = useRef(null);
    const taskListDropdownRef = useRef(null);
    const assigneeSearchRef = useRef(null);
    const assigneeDropdownRef = useRef(null);
    const inputRef = useRef(null);
    const datePickerRef = useRef(null);
    const datePickerWrapperRef = useRef(null);
    const previousDateValueRef = useRef('');

    // Expose focus method to parent component
    useImperativeHandle(ref, () => ({
        focus: () => {
            if (inputRef.current) {
                inputRef.current.focus();
            }
        }
    }));

    // Fetch projects when component mounts or dropdown opens
    useEffect(() => {
        if (showProjectDropdown && projects.length === 0 && !isLoadingProjects) {
            fetchProjects();
        }
    }, [showProjectDropdown]);

    // Fetch task lists when project is selected
    useEffect(() => {
        if (selectedProjectId && !selectedTaskListId) {
            fetchTaskLists(selectedProjectId);
        }
    }, [selectedProjectId]);

    // Initialize with provided project if available
    useEffect(() => {
        if (projectId && project) {
            setSelectedProjectId(projectId);
            setSelectedProject(project);
            if (taskListId) {
                setSelectedTaskListId(taskListId);
            }
        }
    }, [projectId, taskListId, project]);

    const fetchProjects = async () => {
        setIsLoadingProjects(true);
        try {
            const response = await getProjects({ filter: 'active', limit: 100, page: 1 });
            if (response && response.data && response.data.projects) {
                setProjects(response.data.projects);
            }
        } catch (err) {
            console.error('Failed to fetch projects:', err);
        } finally {
            setIsLoadingProjects(false);
        }
    };

    const fetchTaskLists = async (projectId) => {
        setIsLoadingTaskLists(true);
        try {
            const response = await getTaskLists(projectId);
            if (response && response.data) {
                const lists = Array.isArray(response.data) ? response.data : (response.data.task_lists || []);
                setTaskLists(lists);
                // Auto-select first task list if available
                if (lists.length > 0 && !selectedTaskListId) {
                    setSelectedTaskListId(lists[0].id);
                }
            }
        } catch (err) {
            console.error('Failed to fetch task lists:', err);
        } finally {
            setIsLoadingTaskLists(false);
        }
    };

    const handleSelectProject = (project) => {
        setSelectedProjectId(project.id);
        setSelectedProject(project);
        setSelectedTaskListId(null);
        setTaskLists([]);
        setShowProjectDropdown(false);
        // Fetch task lists for the selected project
        fetchTaskLists(project.id);
    };

    const handleSelectTaskList = (taskList) => {
        setSelectedTaskListId(taskList.id);
        setShowTaskListDropdown(false);
    };

    // Filter assignee search from project users or search all users
    useEffect(() => {
        if (!assigneeSearchQuery.trim()) {
            setAssigneeSearchResults([]);
            setShowAssigneeDropdown(false);
            return;
        }

        const debounceTimer = setTimeout(async () => {
            const currentProject = selectedProject || project;
            if (currentProject?.users && currentProject.users.length > 0) {
                // Use project users if available
                const projectUsers = currentProject.users || [];
                const searchLower = assigneeSearchQuery.toLowerCase().trim();
                
                const filteredUsers = projectUsers.filter(user => {
                    const name = (user.display_name || user.name || '').toLowerCase();
                    const email = (user.user_email || user.email || '').toLowerCase();
                    return name.includes(searchLower) || email.includes(searchLower);
                });
                
                setAssigneeSearchResults(filteredUsers);
                setShowAssigneeDropdown(true);
            } else if (searchUsers) {
                // Search all users if no project users
                try {
                    const response = await searchUsers({ search: assigneeSearchQuery, page: 1, size: 10 });
                    if (response && response.data && response.data.users) {
                        const users = response.data.users.users || [];
                        setAssigneeSearchResults(users);
                        setShowAssigneeDropdown(true);
                    } else {
                        setAssigneeSearchResults([]);
                        setShowAssigneeDropdown(true);
                    }
                } catch (err) {
                    console.error('Error searching users:', err);
                    setAssigneeSearchResults([]);
                    setShowAssigneeDropdown(true);
                }
            }
        }, 300);

        return () => clearTimeout(debounceTimer);
    }, [assigneeSearchQuery, selectedProject?.users, project?.users, searchUsers]);

    // Close assignee dropdown, date picker, and project/task list dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                assigneeDropdownRef.current && 
                !assigneeDropdownRef.current.contains(event.target) &&
                assigneeSearchRef.current &&
                !assigneeSearchRef.current.contains(event.target)
            ) {
                setShowAssigneeDropdown(false);
            }
            // Close date picker if clicking outside
            if (showDueDatePicker && 
                datePickerWrapperRef.current && 
                !datePickerWrapperRef.current.contains(event.target) &&
                !event.target.closest('.asana-due-date-wrapper')) {
                setShowDueDatePicker(false);
            }
            // Close project dropdown if clicking outside
            if (showProjectDropdown && 
                projectDropdownRef.current && 
                !projectDropdownRef.current.contains(event.target) &&
                !event.target.closest('.asana-project-selector')) {
                setShowProjectDropdown(false);
            }
            // Close task list dropdown if clicking outside
            if (showTaskListDropdown && 
                taskListDropdownRef.current && 
                !taskListDropdownRef.current.contains(event.target) &&
                !event.target.closest('.asana-task-list-selector')) {
                setShowTaskListDropdown(false);
            }
        };

        if (showAssigneeDropdown || showDueDatePicker || showProjectDropdown || showTaskListDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showAssigneeDropdown, showDueDatePicker, showProjectDropdown, showTaskListDropdown]);

    const handleSelectAssignee = (user) => {
        setAssignee(user);
        setAssigneeSearchQuery('');
        setShowAssigneeDropdown(false);
    };

    const handleSave = () => {
        if (!taskTitle.trim()) {
            alert('Please enter a task title');
            return;
        }
        
        if (!selectedProjectId) {
            alert('Please select a project');
            return;
        }
        
        if (!selectedTaskListId) {
            alert('Please select a task list');
            return;
        }
        
        const taskData = {
            title: taskTitle.trim(),
            assignee: assignee,
            dueDate: dueDate,
            priority: priority
        };
        
        onSave(selectedProjectId, selectedTaskListId, taskData);
        
        // Reset form but keep project and task list selected
        setTaskTitle('');
        setAssignee(null);
        setDueDate('');
        setPriority(0);
        if (inputRef.current) {
            inputRef.current.focus();
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && taskTitle.trim()) {
            e.preventDefault();
            handleSave();
        } else if (e.key === 'Escape') {
            handleCancel();
        }
    };

    const handleCancel = () => {
        setTaskTitle('');
        setAssignee(null);
        setDueDate('');
        setPriority(0);
        // Reset project selection if it was not provided initially
        if (!projectId) {
            setSelectedProjectId(null);
            setSelectedProject(null);
            setSelectedTaskListId(null);
            setTaskLists([]);
        } else {
            setSelectedProjectId(projectId);
            setSelectedProject(project);
            setSelectedTaskListId(taskListId);
        }
        if (onCancel) {
            onCancel();
        }
    };

    const handleBlur = (e) => {

        // Don't cancel if clicking on the save button or other interactive elements
        if (e.relatedTarget && (
            e.relatedTarget.closest('.asana-save-task-arrow-btn') ||
            e.relatedTarget.closest('.asana-assignee-select-wrapper') ||
            e.relatedTarget.closest('.asana-due-date-picker-wrapper') ||
            e.relatedTarget.closest('.asana-remove-assignee') ||
            e.relatedTarget.closest('.asana-remove-due-date') ||
            e.relatedTarget.closest('.asana-project-selector') ||
            e.relatedTarget.closest('.asana-project-dropdown') ||
            e.relatedTarget.closest('.asana-task-list-selector') ||
            e.relatedTarget.closest('.asana-task-list-dropdown') ||
            e.relatedTarget.closest('.asana-priority-dropdown') ||
            e.relatedTarget.closest('.asana-default-task-input-row')
        )) {
            return;
        }
        return;
        // Small delay to allow button clicks to register
        setTimeout(() => {
            if (!taskTitle.trim() && !assignee && !dueDate && !selectedProjectId) {
                handleCancel();
            }
        }, 200);
    };

    // Helper function to format date for date input (yyyy-MM-dd)
    const formatDateForInput = (dateValue) => {
        if (!dateValue) return '';
        try {
            // If it's already in yyyy-MM-dd format, return as is
            if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
                return dateValue;
            }
            // Parse the date and format it
            const date = new Date(dateValue);
            if (isNaN(date.getTime())) return '';
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        } catch (e) {
            return '';
        }
    };

    const handleDateInputChange = (e) => {
        const newDate = e.target.value;
        const previousDate = previousDateValueRef.current;

        // Only update if the date actually changed to a different valid date
        // This prevents auto-selection when navigating months
        if (newDate && newDate !== previousDate && newDate !== dueDate) {
            previousDateValueRef.current = newDate;
            setDueDate(newDate);
        }
    };

    return (
        <tr className="asana-task-row asana-default-task-input-row">
            <td className="asana-col-name">
                <div className="asana-task-name-cell">
                    <input
                        type="checkbox"
                        disabled
                        className="asana-task-checkbox"
                    />
                    {/* Project Selector */}
                    <div className="asana-project-selector" style={{ position: 'relative', display: 'inline-block', marginRight: '8px' }}>
                        {selectedProject ? (
                            <div 
                                className="asana-project-selected"
                                onClick={() => setShowProjectDropdown(true)}
                                style={{ 
                                    display: 'inline-flex', 
                                    alignItems: 'center', 
                                    padding: '4px 8px', 
                                    background: '#f0f0f0', 
                                    borderRadius: '4px', 
                                    cursor: 'pointer',
                                    fontSize: '12px',
                                    border: selectedProjectId ? '1px solid #ddd' : '1px solid #ff6b6b'
                                }}
                            >
                                <span style={{ marginRight: '4px' }}>{selectedProject.title}</span>
                                <i className="fa-solid fa-chevron-down" style={{ fontSize: '10px' }}></i>
                            </div>
                        ) : (
                            <div 
                                className="asana-project-placeholder-clickable"
                                onClick={() => setShowProjectDropdown(true)}
                                style={{ 
                                    display: 'inline-flex', 
                                    alignItems: 'center', 
                                    padding: '4px 8px', 
                                    background: '#fff3cd', 
                                    borderRadius: '4px', 
                                    cursor: 'pointer',
                                    fontSize: '12px',
                                    border: '1px solid #ffc107',
                                    color: '#856404'
                                }}
                                title="Select project (required)"
                            >
                                <i className="fa-solid fa-folder" style={{ marginRight: '4px' }}></i>
                                <span>Select project *</span>
                            </div>
                        )}
                        {showProjectDropdown && (
                            <div 
                                className="asana-project-dropdown" 
                                ref={projectDropdownRef}
                                style={{
                                    position: 'absolute',
                                    top: '100%',
                                    left: 0,
                                    marginTop: '4px',
                                    background: '#fff',
                                    border: '1px solid #ddd',
                                    borderRadius: '6px',
                                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                                    maxHeight: '300px',
                                    overflowY: 'auto',
                                    zIndex: 1000,
                                    minWidth: '200px'
                                }}
                            >
                                {isLoadingProjects ? (
                                    <div style={{ padding: '12px', textAlign: 'center', color: '#666' }}>
                                        <i className="fa-solid fa-spinner fa-spin"></i> Loading...
                                    </div>
                                ) : projects.length > 0 ? (
                                    projects.map((project) => (
                                        <div
                                            key={project.id}
                                            onClick={() => handleSelectProject(project)}
                                            style={{
                                                padding: '12px',
                                                cursor: 'pointer',
                                                borderBottom: '1px solid #f0f0f0',
                                                transition: 'background-color 0.2s'
                                            }}
                                            onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                                            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                                        >
                                            {project.title}
                                        </div>
                                    ))
                                ) : (
                                    <div style={{ padding: '12px', textAlign: 'center', color: '#666' }}>
                                        No projects found
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    {/* Task List Selector (shown when project is selected) */}
                    {selectedProjectId && (
                        <div className="asana-task-list-selector" style={{ position: 'relative', display: 'inline-block', marginRight: '8px' }}>
                            {selectedTaskListId && taskLists.find(tl => tl.id === selectedTaskListId) ? (
                                <div 
                                    className="asana-task-list-selected"
                                    onClick={() => setShowTaskListDropdown(true)}
                                    style={{ 
                                        display: 'inline-flex', 
                                        alignItems: 'center', 
                                        padding: '4px 8px', 
                                        background: '#f0f0f0', 
                                        borderRadius: '4px', 
                                        cursor: 'pointer',
                                        fontSize: '12px',
                                        border: '1px solid #ddd'
                                    }}
                                >
                                    <span style={{ marginRight: '4px' }}>{taskLists.find(tl => tl.id === selectedTaskListId)?.title || 'Task List'}</span>
                                    <i className="fa-solid fa-chevron-down" style={{ fontSize: '10px' }}></i>
                                </div>
                            ) : (
                                <div 
                                    className="asana-task-list-placeholder-clickable"
                                    onClick={() => setShowTaskListDropdown(true)}
                                    style={{ 
                                        display: 'inline-flex', 
                                        alignItems: 'center', 
                                        padding: '4px 8px', 
                                        background: '#fff3cd', 
                                        borderRadius: '4px', 
                                        cursor: 'pointer',
                                        fontSize: '12px',
                                        border: '1px solid #ffc107',
                                        color: '#856404'
                                    }}
                                    title="Select task list (required)"
                                >
                                    {isLoadingTaskLists ? (
                                        <>
                                            <i className="fa-solid fa-spinner fa-spin" style={{ marginRight: '4px' }}></i>
                                            <span>Loading...</span>
                                        </>
                                    ) : (
                                        <>
                                            <i className="fa-solid fa-list" style={{ marginRight: '4px' }}></i>
                                            <span>Select task list *</span>
                                        </>
                                    )}
                                </div>
                            )}
                            {showTaskListDropdown && taskLists.length > 0 && (
                                <div 
                                    className="asana-task-list-dropdown" 
                                    ref={taskListDropdownRef}
                                    style={{
                                        position: 'absolute',
                                        top: '100%',
                                        left: 0,
                                        marginTop: '4px',
                                        background: '#fff',
                                        border: '1px solid #ddd',
                                        borderRadius: '6px',
                                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                                        maxHeight: '300px',
                                        overflowY: 'auto',
                                        zIndex: 1000,
                                        minWidth: '200px'
                                    }}
                                >
                                    {taskLists.map((taskList) => (
                                        <div
                                            key={taskList.id}
                                            onClick={() => handleSelectTaskList(taskList)}
                                            style={{
                                                padding: '12px',
                                                cursor: 'pointer',
                                                borderBottom: '1px solid #f0f0f0',
                                                transition: 'background-color 0.2s'
                                            }}
                                            onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                                            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                                        >
                                            {taskList.title}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                    <input
                        ref={inputRef}
                        type="text"
                        className="asana-default-task-input"
                        placeholder="Write a task name"
                        value={taskTitle}
                        onChange={(e) => setTaskTitle(e.target.value)}
                        onKeyDown={handleKeyDown}
                        onBlur={handleBlur}
                    />
                    {taskTitle.trim() && (
                        <button
                            type="button"
                            className="asana-save-task-arrow-btn"
                            onClick={handleSave}
                            title="Save task"
                        >
                            <i className="fa-solid fa-arrow-right"></i>
                        </button>
                    )}
                    <button
                        type="button"
                        className="asana-cancel-task-btn"
                        onClick={handleCancel}
                        title="Cancel"
                        style={{ marginLeft: '5px', background: 'transparent', border: 'none', cursor: 'pointer', color: '#666' }}
                    >
                        <i className="fa-solid fa-times"></i>
                    </button>
                </div>
            </td>
            <td className="asana-col-assignee">
                <div className="asana-assignee-select-wrapper" ref={assigneeSearchRef}>
                    {assignee ? (
                        <div 
                            className="asana-assignee-selected"
                            onClick={() => {
                                setShowAssigneeDropdown(true);
                                setAssigneeSearchQuery('');
                            }}
                        >
                            <div className="asana-assignee-avatar">
                                {assignee.image || assignee.avatar_url ? (
                                    <img src={assignee.image || assignee.avatar_url} alt={assignee.display_name || assignee.name} />
                                ) : (
                                    <div className="asana-assignee-placeholder">
                                        {(assignee.display_name || assignee.name || 'U').charAt(0).toUpperCase()}
                                    </div>
                                )}
                            </div>
                            <button
                                type="button"
                                className="asana-remove-assignee"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setAssignee(null);
                                }}
                            >
                                <i className="fa-solid fa-times"></i>
                            </button>
                        </div>
                    ) : (
                        <div 
                            className="asana-assignee-placeholder-clickable"
                            onClick={() => setShowAssigneeDropdown(true)}
                        >
                            <i className="fa-solid fa-user-plus"></i>
                        </div>
                    )}
                    {showAssigneeDropdown && (
                        <div className="asana-assignee-dropdown" ref={assigneeDropdownRef}>
                            <input
                                type="text"
                                className="asana-assignee-search-input"
                                placeholder="Search assignee..."
                                value={assigneeSearchQuery}
                                onChange={(e) => setAssigneeSearchQuery(e.target.value)}
                                autoFocus
                            />
                            {assigneeSearchResults.length > 0 ? (
                                <div className="asana-assignee-results">
                                    {assigneeSearchResults.map((user) => (
                                        <div
                                            key={user.ID || user.id}
                                            className="asana-assignee-result-item"
                                            onClick={() => handleSelectAssignee(user)}
                                        >
                                            <div className="asana-assignee-avatar">
                                                {user.image || user.avatar_url ? (
                                                    <img src={user.image || user.avatar_url} alt={user.display_name || user.name} />
                                                ) : (
                                                    <div className="asana-assignee-placeholder">
                                                        {(user.display_name || user.name || 'U').charAt(0).toUpperCase()}
                                                    </div>
                                                )}
                                            </div>
                                            <span>{user.display_name || user.name || 'User'}</span>
                                        </div>
                                    ))}
                                </div>
                            ) : assigneeSearchQuery.trim() ? (
                                <div className="asana-search-empty">No users found</div>
                            ) : (selectedProject || project)?.users && (selectedProject || project).users.length > 0 ? (
                                <div className="asana-assignee-results">
                                    {(selectedProject || project).users.map((user) => (
                                        <div
                                            key={user.ID || user.id}
                                            className="asana-assignee-result-item"
                                            onClick={() => handleSelectAssignee(user)}
                                        >
                                            <div className="asana-assignee-avatar">
                                                {user.image || user.avatar_url ? (
                                                    <img src={user.image || user.avatar_url} alt={user.display_name || user.name} />
                                                ) : (
                                                    <div className="asana-assignee-placeholder">
                                                        {(user.display_name || user.name || 'U').charAt(0).toUpperCase()}
                                                    </div>
                                                )}
                                            </div>
                                            <span>{user.display_name || user.name || 'User'}</span>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="asana-search-empty">Start typing to search users</div>
                            )}
                        </div>
                    )}
                </div>
            </td>
            <td className="asana-col-due">
                <div className="asana-due-date-wrapper">
                    {dueDate ? (
                        <div 
                            className="asana-due-date-selected"
                            onClick={() => setShowDueDatePicker(true)}
                        >
                            <span>{new Date(dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                            <button
                                type="button"
                                className="asana-remove-due-date"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setDueDate('');
                                }}
                            >
                                <i className="fa-solid fa-times"></i>
                            </button>
                        </div>
                    ) : (
                        <div 
                            className="asana-due-date-placeholder-clickable"
                            onClick={() => setShowDueDatePicker(true)}
                        >
                            <i className="fa-solid fa-calendar"></i>
                        </div>
                    )}
                    {showDueDatePicker && (
                        <div className="asana-due-date-picker-wrapper" ref={datePickerWrapperRef}>
                            <input
                                ref={datePickerRef}
                                type="date"
                                className="asana-due-date-picker"
                                value={formatDateForInput(dueDate)}
                                onChange={handleDateInputChange}
                                autoFocus
                            />
                        </div>
                    )}
                </div>
            </td>
            <td className="asana-col-priority">
                <PriorityDropdown
                    currentPriority={priority}
                    onPriorityChange={setPriority}
                    className="asana-priority-wrapper"
                    showAsPill={true}
                    handleShowDropdown={false}
                />
            </td>
            <td className="asana-col-actions">
                {/* Empty cell for actions column */}
            </td>
        </tr>
    );
});

const MyTasksView = () => {
    const jwtToken = useSelector((state) => state.auth.jwtToken);
    const [activeFilter, setActiveFilter] = useState('active');
    const [showCompletedTasks, setShowCompletedTasks] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [tasks, setTasks] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedTaskId, setSelectedTaskId] = useState(null);
    const [selectedProjectId, setSelectedProjectId] = useState(null);
    const [projectUsers, setProjectUsers] = useState([]);
    const [showAddTaskRow, setShowAddTaskRow] = useState(false);
    const defaultTaskInputRef = useRef(null);
    const PAGE_SIZE = 50; // Constant page size
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalCount: 0,
        pageSize: PAGE_SIZE,
        hasNext: false,
        hasPrev: false,
    });
    const currentUserId = getUserId();

    // Helper function to update URL with project and task parameters
    const updateURLWithTask = (projectId, taskId) => {
        const url = new URL(window.location.href);

        console.log('updateURLWithTask', projectId, taskId);
        
        if (!taskId || !projectId) {
            // Remove both parameters from URL explicitly
            url.searchParams.delete('task');
            url.searchParams.delete('project');
            window.history.replaceState({}, '', url.toString());
            return;
        }
        
        // Add or update both parameters in URL
        url.searchParams.set('project', projectId);
        url.searchParams.set('task', taskId);
        window.history.replaceState({}, '', url.toString());
    };

    // Helper function to get project ID and task ID from URL
    const getIdsFromURL = () => {
        const urlParams = new URLSearchParams(window.location.search);
        return {
            projectId: urlParams.get('project'),
            taskId: urlParams.get('task')
        };
    };

    // Fetch tasks
    const fetchTasks = async (pageNumber = 1) => {
        setIsLoading(true);
        try {
            const filter = showCompletedTasks ? 'all' : 'active';
            const response = await getMyTasks({
                filter: filter,
                search: searchQuery,
                limit: PAGE_SIZE,
                page: pageNumber
            });
            
            if (response && response.data) {
                // Handle paginated response
                if (response.data.tasks && response.data.pagination) {
                    setTasks(response.data.tasks);
                    const apiPagination = response.data.pagination;
                    setPagination({
                        currentPage: apiPagination.currentPage || pageNumber,
                        totalPages: apiPagination.totalPages || 1,
                        totalCount: apiPagination.totalCount || 0,
                        pageSize: apiPagination.pageSize || PAGE_SIZE,
                        hasNext: (apiPagination.currentPage || pageNumber) < (apiPagination.totalPages || 1),
                        hasPrev: (apiPagination.currentPage || pageNumber) > 1,
                    });
                } else if (Array.isArray(response.data)) {
                    // Fallback for non-paginated response
                    setTasks(response.data);
                    setPagination(prev => ({
                        ...prev,
                        currentPage: pageNumber,
                        totalPages: 1,
                        hasNext: false,
                        hasPrev: false,
                    }));
                } else {
                    setTasks([]);
                }
            } else {
                setTasks([]);
            }
        } catch (err) {
            console.error('Failed to fetch tasks:', err);
            setTasks([]);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        // Reset to page 1 when filter or search changes
        setPagination(prev => ({ ...prev, currentPage: 1 }));
        fetchTasks(1);
    }, [activeFilter, searchQuery, showCompletedTasks]);

    // Check URL for project and task parameters on mount and after tasks are loaded
    useEffect(() => {
        const { projectId: projectIdFromURL, taskId: taskIdFromURL } = getIdsFromURL();
            
        if (taskIdFromURL && projectIdFromURL) {
            // Find the task in the loaded tasks
            setSelectedTaskId(taskIdFromURL);
            setSelectedProjectId(projectIdFromURL);
        } 
    }, [isLoading, tasks]);

    // Handle page change
    const handlePageChange = (page) => {
        if (page !== pagination.currentPage && !isLoading) {
            setPagination(prev => ({ ...prev, currentPage: page }));
            fetchTasks(page);
            // Scroll to top when page changes
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };
    const fetchTaskById = async (projectId, taskId) => {
        try {
            const response = await getTaskById(projectId, taskId);
            if (response && response.data) {
                setTasks(prev => prev.map(task => task.id === taskId ? response.data : task));
            }
        } catch (err) {
            console.error('Failed to fetch task by id:', err);
            return null;
        }
    };

    const handleUpdateTask = async (projectId, taskId, taskListId, taskData = null) => {
        try {
            if (taskData) {
                const response = await updateTask(projectId, taskId, taskData);
                if (response && response.data) {
                    await fetchTaskById(projectId, taskId);
                }
            } else {
                await fetchTaskById(projectId, taskId);
            }
           
        } catch (err) {
            console.error('Failed to update task:', err);
        }
    };

    const handleDeleteTask = async (projectId, taskId, taskListId) => {
        try {
            await deleteTask(projectId, taskListId, taskId);
            await fetchTasks(pagination.currentPage);
            return true;
        } catch (err) {
            console.error('Failed to delete task:', err);
            return false;
        }
    };

    const handleAddAssigneeToTask = async (projectId, taskId, taskListId, assigneeId) => {
        try {
            await addAssigneeToTask(projectId, taskId, assigneeId);
            await fetchTaskById(projectId, taskId);
        } catch (err) {
            console.error('Failed to add assignee:', err);
        }
    };

    const handleOpenTaskDetail = (projectId, taskId) => {
        setSelectedProjectId(projectId);
        setSelectedTaskId(taskId);
        updateURLWithTask(projectId, taskId);
    };

    const handleCloseTaskDetail = () => {
        // Remove both project and task from URL first
        updateURLWithTask(null, null);
        // Then clear the selected task and project
        setSelectedTaskId(null);
        setSelectedProjectId(null);
        fetchTasks(pagination.currentPage);
    };

    const handleOnNotFound = () => {
        setSelectedTaskId(null);
        setSelectedProjectId(null);
        updateURLWithTask(null, null);
    };

    const handleRefreshMainTaskData = () => {
        fetchTasks(pagination.currentPage);
    };

    // Handle create subtask
    const handleCreateSubtask = async (projectId, parentTaskId, taskListId, subtaskData) => {
        if (!jwtToken || !projectId || !parentTaskId || !taskListId) return;

        console.log('handleCreateSubtask', projectId, parentTaskId, taskListId, subtaskData);

        try {
            const createData = {
                title: subtaskData.title,
                description: subtaskData.description || '',
                task_list_id: taskListId,
                parent_id: parentTaskId,
            };

            const response = await createTask(projectId, createData);
            if (response && response.data) {
                await fetchTaskById(projectId, parentTaskId);
            } else {
                alert('Failed to create subtask. Please try again.');
            }
        } catch (err) {
            console.error('Failed to create subtask:', err);
            alert('Failed to create subtask. Please try again.');
        }
    };

    // Handle update subtask
    const handleUpdateSubtask = async (projectId, subtaskId, listId, subtaskData) => {
        try {
            const response = await updateTask(projectId, subtaskId, subtaskData);
            if (response && response.data) {
                if(subtaskData?.parent_task_id) {   
                    await fetchTaskById(projectId, subtaskData.parent_task_id);
                }else{
                    await fetchTaskById(projectId, subtaskId);
                }
            } else {
                alert('Failed to update subtask. Please try again.');
            }
        } catch (err) {
            console.error('Failed to update subtask:', err);
            alert('Failed to update subtask. Please try again.');
        }
    };

    // Handle delete subtask
    const handleDeleteSubtask = async (projectId, subtaskId, taskListId, parentTaskId) => {
        // Find the parent task to get projectId
        try {
            const response = await deleteTask(projectId, taskListId, subtaskId);
            if (response && response.data) {
                await fetchTaskById(projectId, parentTaskId);
            } else {
                alert('Failed to delete subtask. Please try again.');
            }
        } catch (err) {
            console.error('Failed to delete subtask:', err);
            alert('Failed to delete subtask. Please try again.');
        }
    };

    // Handle quick add task (focus the default input row)
    const handleQuickAddTask = () => {
        setShowAddTaskRow(true);
        setTimeout(() => {
            if (defaultTaskInputRef.current && typeof defaultTaskInputRef.current.focus === 'function') {
                defaultTaskInputRef.current.focus();
            }
        }, 100);
    };

    // Handle cancel add task row
    const handleCancelAddTaskRow = () => {
        setShowAddTaskRow(false);
    };

    // Handle save draft task (create new task)
    const handleSaveDraftTask = async (projectId, taskListId, taskData) => {
        if (!jwtToken || !projectId || !taskListId) {
            alert('Unable to create task: Missing project or task list information.');
            return;
        }
        
        const title = taskData.title;
        if (!title || !title.trim()) {
            alert('Please enter a task title');
            return;
        }

        try {
            const createData = {
                title: title.trim(),
                description: '',
                task_list_id: taskListId,
                due_date: taskData.dueDate || taskData.due_date || undefined,
                assignees: (taskData.assignee ? [taskData.assignee.ID || taskData.assignee.id] : undefined) || 
                          (taskData.assignees ? taskData.assignees.map(a => a.ID || a.id) : undefined),
                priority: taskData.priority || 0
            };

            const response = await createTask(projectId, createData);
            if (response && response.data) {
                // Refresh tasks
                await fetchTasks(pagination.currentPage);

                setShowAddTaskRow(false);
                // Keep the input row visible for adding another task
                // if (defaultTaskInputRef.current && typeof defaultTaskInputRef.current.focus === 'function') {
                //     setTimeout(() => {
                //        // defaultTaskInputRef.current.focus();
                //     }, 100);
                // }
            } else {
                alert('Failed to create task. Please try again.');
            }
        } catch (err) {
            console.error('Failed to create task:', err);
            alert('Failed to create task. Please try again.');
        }
    };

    return (
        <div className="octopus-projects-manager asana-style">
            <div className="octopus-projects-manager-header">
                <h2 className="octopus-projects-manager-title">My Tasks</h2>
                <div className="octopus-projects-manager-actions">
                    <button
                        className="asana-add-task-btn"
                        onClick={handleQuickAddTask}
                        style={{ marginRight: '10px' }}
                    >
                        <i className="fa-solid fa-plus"></i>
                        Add task
                    </button>
                    <div className="octopus-tasks-show-completed-toggle">
                        <label className="octopus-toggle-switch">
                            <input
                                type="checkbox"
                                checked={showCompletedTasks}
                                onChange={(e) => setShowCompletedTasks(e.target.checked)}
                            />
                            <span className="octopus-toggle-slider"></span>
                        </label>
                        <span className="octopus-toggle-label">Show completed tasks</span>
                    </div>
                    <div className="octopus-projects-search">
                        <input
                            type="text"
                            placeholder="Search tasks ..."
                            className="octopus-projects-search-input"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                </div>
            </div>


            {/* Tasks Content */}
            <div className="octopus-projects-content">
                {isLoading ? (
                    <div className="octopus-projects-empty">
                        <i className="fa-solid fa-spinner fa-spin"></i>
                        <p>Loading tasks...</p>
                    </div>
                ) : tasks.length > 0 ? (
                    <>
                        <div className="asana-tasks-table">
                            <table>
                                <thead>
                                    <tr>
                                        <th className="asana-col-name">Name</th>
                                        <th className="asana-col-assignee">Assignee</th>
                                        <th className="asana-col-due">Due-date</th>
                                        <th className="asana-col-priority">Priority</th>
                                        <th className="asana-col-actions">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {/* Default task input row - shown when Add task button is clicked */}
                                    {showAddTaskRow && (
                                        <DefaultTaskInputRow
                                            ref={defaultTaskInputRef}
                                            projectId={null}
                                            taskListId={null}
                                            project={null}
                                            onSave={handleSaveDraftTask}
                                            searchUsers={searchUsers}
                                            onCancel={handleCancelAddTaskRow}
                                        />
                                    )}
                                    {tasks
                                        .filter(task => !task.parent_id || task.parent_id === 0)
                                        .map((task) => (
                                            <EditableTaskRow
                                                key={task.id}
                                                task={task}
                                                taskListId={task.task_list_id}
                                                project={task.project}
                                                projectId={task.project_id}
                                                onUpdate={handleUpdateTask}
                                                onDelete={handleDeleteTask}
                                                addAssigneeToTask={handleAddAssigneeToTask}
                                                searchUsers={searchUsers}
                                                onOpenDetail={handleOpenTaskDetail}
                                                onCreateSubtask={handleCreateSubtask}
                                                onUpdateSubtask={handleUpdateSubtask}
                                                onDeleteSubtask={handleDeleteSubtask}
                                                onDragStart={undefined}
                                                onDragOver={undefined}
                                                onDrop={undefined}
                                                onDragEnd={undefined}
                                                isDragging={false}
                                                isDragOver={false}
                                                DefaultShowSubtasks={true}
                                            />
                                        ))}
                                </tbody>
                            </table>
                        </div>
                        
                        {/* Pagination */}
                        <Pagination
                            currentPage={pagination.currentPage}
                            totalPages={pagination.totalPages}
                            hasNext={pagination.hasNext}
                            hasPrev={pagination.hasPrev}
                            onPageChange={handlePageChange}
                            isLoading={isLoading}
                        />
                    </>
                ) : (
                    <div className="octopus-projects-empty">
                        <p>No tasks found. {searchQuery ? 'Try a different search query.' : 'Create a new task to get started!'}</p>
                    </div>
                )}
                
            </div>

            {/* Task Detail Side Panel */}
            {selectedTaskId && selectedProjectId && (
                <TaskDetailSidePanel
                    taskId={selectedTaskId}
                    projectId={selectedProjectId}
                    currentUserId={currentUserId}
                    onClose={handleCloseTaskDetail}
                    onOpenSubtaskDetail={handleOpenTaskDetail}
                    onRefreshMainTaskData={handleRefreshMainTaskData}
                    OnNotFound={handleOnNotFound}
                />
            )}
        </div>
    );
};

export default MyTasksView;
